import { useState } from "react";
import { ChevronDown } from "lucide-react";

const GRADIENTS = [
  "from-fuchsia-500 to-rose-500", "from-emerald-400 to-lime-400", "from-amber-400 to-emerald-500", "from-rose-400 to-amber-300",
  "from-sky-400 to-violet-500", "from-violet-500 to-fuchsia-500", "from-orange-400 to-rose-500", "from-yellow-300 to-orange-500",
];

function GenreCard({ genre, index, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`genre-${genre.key}`}
      aria-pressed={active}
      onClick={onClick}
      className={`group relative min-h-[84px] overflow-hidden rounded-2xl border px-4 py-3 text-left transition-transform duration-200 ${
        active ? "border-transparent text-white shadow-[0_0_28px_rgba(99,102,241,0.35)]" : "border-white/10 bg-white/[0.03] text-white/75 hover:-translate-y-0.5 hover:bg-white/[0.07]"
      }`}
    >
      {active ? <span aria-hidden className={`absolute inset-0 bg-gradient-to-br ${GRADIENTS[index % GRADIENTS.length]} opacity-90`} /> : null}
      <span className="relative block font-display text-[13px] font-semibold leading-snug sm:text-sm">{genre.label}</span>
      {genre.bpm ? <span className={`relative mt-1 block font-mono text-[10px] uppercase tracking-wider ${active ? "text-white/85" : "text-white/40"}`}>~{genre.bpm} BPM</span> : null}
    </button>
  );
}

/** 8 gospel genres up front; everything else (and a free-text custom style) under "More genres". */
export default function GenrePicker({ catalog, genreKey, customStyle, onChange }) {
  const [moreOpen, setMoreOpen] = useState(() => genreKey === "custom" || catalog.more_genres.some((g) => g.key === genreKey));
  const pill = (key, label) => (
    <button
      key={key}
      type="button"
      data-testid={`genre-${key}`}
      aria-pressed={genreKey === key}
      onClick={() => onChange({ genre_key: key })}
      className={`rounded-full border px-3.5 py-1.5 text-[13px] transition-colors duration-200 ${
        genreKey === key ? "border-blue-400/60 bg-blue-500/25 text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.08]"
      }`}
    >
      {label}
    </button>
  );
  return (
    <section data-testid="genre-picker">
      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">Genre</p>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {catalog.gospel_genres.map((g, i) => <GenreCard key={g.key} genre={g} index={i} active={genreKey === g.key} onClick={() => onChange({ genre_key: g.key })} />)}
      </div>
      <button
        type="button"
        data-testid="more-genres-toggle"
        onClick={() => setMoreOpen((o) => !o)}
        aria-expanded={moreOpen}
        className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50 transition-colors hover:text-white"
      >
        More genres <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`} />
      </button>
      {moreOpen ? (
        <div data-testid="more-genres" className="mt-3 flex flex-wrap gap-2">
          {catalog.more_genres.map((g) => pill(g.key, g.label))}
          {pill("custom", "Custom style")}
        </div>
      ) : null}
      {genreKey === "custom" ? (
        <input
          data-testid="custom-style-input"
          value={customStyle || ""}
          onChange={(e) => onChange({ custom_style: e.target.value })}
          maxLength={300}
          placeholder="Describe your style — e.g. slow gospel ballad with strings and a choir"
          className="mt-3 w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-blue-400/60 focus:outline-none"
        />
      ) : null}
    </section>
  );
}
